import { JSX, useEffect } from "react";
import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Spinner } from "@via/components/loading/loading.tsx";
import { useForm } from "react-hook-form";
import { CreateUserRequestBody, LoginType, UserRole } from "common";
import {
  createUserFormValidationOptions,
  requiredValidation,
} from "@via/utils/form-validators.tsx";
import FormInputComponent from "@via/components/inputs/form-input-component.tsx";
import {
  USER_LOGIN_TYPE_OPTIONS,
  USER_ROLE_OPTIONS,
} from "@via/utils/constants.tsx";
import SelectComponent from "@via/components/select/select.tsx";
import { useCreateUser } from "@via/pages/account/admin/admin.api.tsx";
import { removeFalsyValues } from "@via/utils/helpers.tsx";

export default function CreateUserForm({
  setOpen,
}: {
  setOpen: (open: boolean) => void;
}): JSX.Element {
  const {
    mutate: createUser,
    isPending: isCreateUserLoading,
    isSuccess: isCreateUserSuccess,
  } = useCreateUser();

  const { control, handleSubmit, watch, trigger } = useForm<
    CreateUserRequestBody & { confirmPassword?: string; loginType?: string }
  >({
    mode: "onChange",
    shouldUnregister: true,
    defaultValues: {
      role: UserRole.User,
      loginType: LoginType.PASSWORD,
    },
  });

  const loginType = watch("loginType");
  const isPasswordRequired = loginType === LoginType.PASSWORD;
  const password = watch("password");
  const confirmPassword = watch("confirmPassword");

  useEffect(() => {
    if (control.getFieldState("confirmPassword").isTouched || confirmPassword) {
      trigger("confirmPassword");
    }
  }, [password, trigger, control, watch]);

  const handleCreateUserForm = (
    payload: CreateUserRequestBody & {
      confirmPassword?: string;
      loginType?: string;
    }
  ) => {
    const formattedPayload = removeFalsyValues(
      payload
    ) as CreateUserRequestBody & {
      confirmPassword?: string;
      loginType?: string;
    };
    delete formattedPayload.confirmPassword;
    delete formattedPayload.loginType;
    createUser(formattedPayload);
  };

  useEffect(() => {
    isCreateUserSuccess && handleClose();
  }, [isCreateUserSuccess]);

  const onSubmit = handleSubmit(handleCreateUserForm);
  const handleClose = () => setOpen(false);

  return (
    <form
      className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl"
      onSubmit={e => {
        e.preventDefault();
        onSubmit(e);
      }}>
      <div className="h-0 flex-1 overflow-y-auto">
        <div className="border-b bg-gray-50 px-4 py-6 sm:px-6">
          <div className="flex items-center justify-between">
            <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">
              User
            </Dialog.Title>
            <div className="ml-3 flex h-7 items-center">
              <button
                type="button"
                className="relative rounded-md text-gray-900 duration-150 ease-in-out hover:text-gray-600 focus:outline-none"
                onClick={handleClose}>
                <span className="absolute -inset-2.5" />
                <span className="sr-only">Close panel</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>
          <div className="mt-1">
            <p className="text-sm text-gray-500">Create a new user.</p>
          </div>
        </div>
        <div className="flex flex-1 flex-col justify-between pb-10">
          <div className="divide-y divide-gray-200 px-4 sm:px-6">
            <div className="space-y-6 pb-5 pt-6">
              <FormInputComponent
                control={control}
                name="username"
                label="Username"
                placeholder="Enter Username"
                validations={requiredValidation("Username")}
                showRequiredField
              />
              <FormInputComponent
                control={control}
                name="name"
                label="Name"
                placeholder="Enter name"
                validations={requiredValidation("Name")}
                showRequiredField
              />
              <FormInputComponent
                control={control}
                name="email"
                label="Email"
                placeholder="Enter email"
                validations={requiredValidation("Email")}
                showRequiredField
              />
              <FormInputComponent
                control={control}
                name="institute"
                label="Institute"
                placeholder="Enter institute"
                validations={requiredValidation("Institute")}
                showRequiredField
              />
              <FormInputComponent
                control={control}
                name="lab"
                label="Lab"
                placeholder="Enter lab"
                validations={requiredValidation("Lab")}
                showRequiredField
              />
              <div>
                <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
                  Role
                </label>
                <SelectComponent
                  control={control}
                  name="role"
                  placeholder="Choose User Role"
                  options={USER_ROLE_OPTIONS}
                  enableClear={false}
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
                  Login Type
                </label>
                <SelectComponent
                  control={control}
                  name="loginType"
                  placeholder="Choose Login Type"
                  options={USER_LOGIN_TYPE_OPTIONS}
                  enableClear={false}
                />
              </div>

              {isPasswordRequired ? (
                <div className="space-y-6">
                  <FormInputComponent
                    control={control}
                    type={"password"}
                    name="password"
                    label="Password"
                    placeholder="Enter Password"
                    tooltipText={
                      "Required when 'Login Type' is set as 'password'."
                    }
                    showTooltipIcon
                    validations={createUserFormValidationOptions.password}
                    showRequiredField={isPasswordRequired}
                  />
                  <FormInputComponent
                    control={control}
                    type={"password"}
                    name="confirmPassword"
                    label="Password Confirmation"
                    placeholder="Enter Password Confirmation"
                    tooltipText={
                      "Required when 'Login Type' is set as 'password'."
                    }
                    showTooltipIcon
                    validations={{
                      ...createUserFormValidationOptions.confirmPassword,
                      validate: value =>
                        value === password || "Passwords must match",
                    }}
                    showRequiredField={isPasswordRequired}
                  />
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-shrink-0 justify-end px-4 py-4">
        <button
          id="create-user-details-cancel"
          type="button"
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          onClick={handleClose}>
          Cancel
        </button>
        <button
          id="create-user-details-save"
          type="submit"
          className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
          <Spinner loading={isCreateUserLoading}>Create</Spinner>
        </button>
      </div>
    </form>
  );
}
